import { SEO } from "@/components/SEO";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  ArrowRight,
  ChevronDown,
  Check,
  Users,
  Bot,
  Building2,
  Package,
  Plug,
} from "lucide-react";

const CANONICAL = "https://cargonova.ai/pricing";
const CANONICAL_ZH = "https://cargonova.ai/zh/pricing";
const CANONICAL_TH = "https://cargonova.ai/th/pricing";
const PUBLISHED = "2026-07-20";
const UPDATED = "2026-07-20";

const articleSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline: "CargoNova Pricing: Transparent Cloud Platform Costs for Freight Forwarders",
  description:
    "CargoNova uses a base subscription plus usage-based pricing model. Learn what factors affect cost and how it compares to legacy TMS licensing.",
  datePublished: PUBLISHED,
  dateModified: UPDATED,
  author: { "@type": "Organization", name: "CargoNova" },
  publisher: { "@id": "https://cargonova.ai/#organization" },
  mainEntityOfPage: CANONICAL,
  inLanguage: "en",
  about: [
    "Freight forwarding software pricing",
    "Cloud platform subscription",
    "CargoNova cost",
  ],
};

const contentEn = {
  eyebrow: "Pricing",
  title: "CargoNova Pricing —\nPredictable, Built for Forwarders",
  summary:
    "CargoNova is priced as a cloud platform so you can start small and scale as your shipment volume grows. No large upfront license or hidden module fees.",
  intro:
    "Most teams begin with a base subscription covering core users and standard AI agents, then add usage-based components for extra shipments, additional agents or deeper integrations.",

  modelTitle: "How CargoNova Pricing Works",
  models: [
    {
      title: "Base subscription",
      body: "Covers your tenant, core users, standard support and the foundational platform. Ideal for getting your first office live quickly.",
    },
    {
      title: "Usage-based components",
      body: "Scales with your business: shipment volume, extra AI agents, additional offices and premium integrations.",
    },
    {
      title: "Implementation services",
      body: "Optional onboarding, data migration and training packages tailored to your current system landscape.",
    },
  ],

  factorsTitle: "What Affects Your Price",
  factors: [
    { icon: Users, label: "Number of users", desc: "Operators, supervisors and managers accessing the platform." },
    { icon: Bot, label: "AI agents enabled", desc: "Each agent can be activated independently as you grow." },
    { icon: Building2, label: "Offices & branches", desc: "Multi-branch deployments add tenant configuration and governance." },
    { icon: Package, label: "Shipment volume", desc: "Higher volumes benefit from usage-based economies of scale." },
    { icon: Plug, label: "Integration depth", desc: "Standard connectors are included; custom ERP/TMS bridges may add cost." },
  ],

  compareTitle: "CargoNova vs Legacy TMS Pricing",
  compareHeaders: ["", "Legacy TMS", "CargoNova"],
  compareRows: [
    { label: "Upfront license", legacy: "Often large initial fee", cargonova: "Low or no upfront cost" },
    { label: "User licensing", legacy: "Per-seat add-ons", cargonova: "Flexible user tiers" },
    { label: "Module fees", legacy: "Extra for customs, finance, etc.", cargonova: "Agents enabled as needed" },
    { label: "Forecasting", legacy: "Hard to predict", cargonova: "Monthly or annual predictability" },
  ],

  benefitsTitle: "Why Forwarders Prefer This Model",
  benefits: [
    { metric: "Low", title: "Upfront risk", body: "Start with a focused scope and expand without a large capital commitment." },
    { metric: "Elastic", title: "Scales with volume", body: "Costs rise with actual usage, not with headcount alone." },
    { metric: "Clear", title: "No surprise fees", body: "Transparent subscription and usage rates help you budget accurately." },
  ],

  faqTitle: "Pricing Questions",
  faqs: [
    {
      q: "Is there a free trial?",
      a: "We typically start with a discovery call and tailored demo. Pilots are available for qualified forwarders after the initial evaluation.",
    },
    {
      q: "Do I pay for every user?",
      a: "Pricing is tiered by user bands, so small teams are not penalized for adding one extra operator during busy seasons.",
    },
    {
      q: "Are integrations included?",
      a: "Standard connectors to common carriers, e-commerce platforms and accounting systems are included. Complex custom ERP integrations may require additional services.",
    },
  ],

  ctaTitle: "Get a tailored quote",
  ctaBody:
    "Tell us about your team size, shipment volume and integration needs. We'll prepare a clear proposal with no hidden fees.",
  ctaButton: "Request pricing",
  ctaLink: "https://www.deep-insights.ai/book-demo",
  backLabel: "Back to Home",
  backTo: "/",
  disclaimer:
    "Pricing information is for reference only. Final rates are confirmed in a written proposal based on your specific requirements.",
};

const contentZh = {
  eyebrow: "价格计费",
  title: "CargoNova 价格 —\n可预期的成本，为货代企业设计",
  summary:
    "CargoNova 采用云平台订阅模式，您可以从小规模起步，随业务量增长灵活扩展，无需一次性高额授权或隐藏模块费用。",
  intro:
    "大多数团队从基础订阅开始，覆盖核心用户和标准 AI Agent；随后根据货量、额外 Agent 或深度集成需求叠加按用量计费部分。",

  modelTitle: "CargoNova 计费模式",
  models: [
    {
      title: "基础订阅",
      body: "覆盖租户环境、核心用户、标准支持与基础平台能力，适合快速让首个办公室上线。",
    },
    {
      title: "按用量计费",
      body: "随业务增长而弹性扩展：货量、额外 AI Agent、更多办公网点和高级集成。",
    },
    {
      title: "实施服务",
      body: "可选的上线、数据迁移与培训方案，根据您现有系统环境量身定制。",
    },
  ],

  factorsTitle: "影响价格的因素",
  factors: [
    { icon: Users, label: "用户数量", desc: "使用平台的运营、主管和管理人员数量。" },
    { icon: Bot, label: "启用的 AI Agent", desc: "每位 Agent 可独立启用，按需叠加。" },
    { icon: Building2, label: "办公网点", desc: "多分支部署会增加租户配置与治理需求。" },
    { icon: Package, label: "业务量", desc: "货量越高，越能享受用量计费的规模效应。" },
    { icon: Plug, label: "集成深度", desc: "标准连接器已包含，复杂 ERP/TMS 对接可能额外计费。" },
  ],

  compareTitle: "CargoNova 与传统 TMS 计费对比",
  compareHeaders: ["", "传统 TMS", "CargoNova"],
  compareRows: [
    { label: "前期授权", legacy: "通常需一次性高额费用", cargonova: "低甚至无前期成本" },
    { label: "用户授权", legacy: "按席位叠加", cargonova: "灵活的用户梯度" },
    { label: "模块费用", legacy: "关务、财务等模块额外收费", cargonova: "按需启用 Agent" },
    { label: "成本预测", legacy: "难以预估", cargonova: "月度或年度成本可预测" },
  ],

  benefitsTitle: "为什么货代企业青睐这种模式",
  benefits: [
    { metric: "低", title: "前期风险", body: "先聚焦核心场景，再逐步扩展，无需大额资本投入。" },
    { metric: "弹性", title: "随货量扩展", body: "成本随实际业务量增长，而非单纯按人头递增。" },
    { metric: "透明", title: "无隐藏费用", body: "清晰的订阅与用量费率，便于准确预算。" },
  ],

  faqTitle: "价格常见问题",
  faqs: [
    {
      q: "是否提供免费试用？",
      a: "我们通常先安排需求沟通与定制演示。符合条件的货代企业可在初步评估后进入试点阶段。",
    },
    {
      q: "是否需要为每个用户付费？",
      a: "价格按用户梯度计费，小团队在旺季临时增加一两位操作员也不会带来过高成本。",
    },
    {
      q: "集成是否包含在费用内？",
      a: "常见船司、电商平台与财务系统的标准连接器已包含；复杂的定制 ERP 集成可能需要额外服务。",
    },
  ],

  ctaTitle: "获取定制报价",
  ctaBody:
    "告诉我们您的团队规模、货量和集成需求，我们将提供一份清晰、无隐藏费用的报价方案。",
  ctaButton: "申请报价",
  ctaLink: "https://www.deep-insights.ai/book-demo",
  backLabel: "返回首页",
  backTo: "/zh",
  disclaimer:
    "以上价格信息仅供参考，最终价格以基于您具体需求出具的正式商务方案为准。",
};

const contentTh = {
  eyebrow: "ราคาและค่าบริการ",
  title: "CargoNova Pricing —\nราคาที่คาดการณ์ได้ ออกแบบเพื่อฟอร์วาร์ดเดอร์",
  summary:
    "CargoNova ใช้โมเดลสมัครสมาชิกบนคลาวด์แพลตฟอร์ม ให้คุณเริ่มต้นในขอบเขตเล็กและขยายตามปริมาณงานขนส่ง โดยไม่ต้องจ่ายค่าไลเซนส์ก้อนใหญ่ล่วงหน้าและไม่มีค่าโมดูลแอบแฝง",
  intro:
    "ทีมส่วนใหญ่เริ่มจากแพ็กเกจสมัครสมาชิกพื้นฐานที่ครอบคลุมผู้ใช้หลักและ AI Agent มาตรฐาน จากนั้นจึงเพิ่มส่วนที่คิดตามการใช้งานจริง เช่น ปริมาณงานขนส่ง จำนวน Agent เพิ่มเติม หรือการเชื่อมต่อระบบที่ลึกยิ่งขึ้น",

  modelTitle: "โมเดลราคาของ CargoNova",
  models: [
    {
      title: "แพ็กเกจสมัครสมาชิกพื้นฐาน",
      body: "ครอบคลุมสภาพแวดล้อมองค์กร ผู้ใช้หลัก การซัพพอร์ตมาตรฐาน และความสามารถพื้นฐานของแพลตฟอร์ม เหมาะสำหรับการเปิดใช้งานสำนักงานแรกอย่างรวดเร็ว",
    },
    {
      title: "ค่าบริการตามการใช้งานจริง",
      body: "ปรับตามการเติบโตของธุรกิจ ทั้งปริมาณงานขนส่ง จำนวน AI Agent เพิ่มเติม สำนักงานสาขาที่มากขึ้น และการเชื่อมต่อระดับพรีเมียม",
    },
    {
      title: "บริการวางระบบ",
      body: "แพ็กเกจการอบรม การย้ายข้อมูล และการเปิดใช้งาน ที่ปรับให้เหมาะกับสภาพแวดล้อมระบบเดิมของคุณ",
    },
  ],

  factorsTitle: "ปัจจัยที่มีผลต่อราคา",
  factors: [
    { icon: Users, label: "จำนวนผู้ใช้งาน", desc: "จำนวนพนักงานปฏิบัติการ หัวหน้างาน และผู้จัดการที่เข้าใช้งานแพลตฟอร์ม" },
    { icon: Bot, label: "AI Agent ที่เปิดใช้", desc: "แต่ละ Agent สามารถเปิดใช้งานแยกกันได้ตามความจำเป็นของธุรกิจ" },
    { icon: Building2, label: "สำนักงานและสาขา", desc: "การใช้งานหลายสาขาจะเพิ่มการตั้งค่าองค์กรและการกำกับดูแล" },
    { icon: Package, label: "ปริมาณงานขนส่ง", desc: "ปริมาณงานที่สูงขึ้นจะได้ประโยชน์จากอัตราค่าใช้งานที่ประหยัดกว่า" },
    { icon: Plug, label: "ระดับการเชื่อมต่อระบบ", desc: "คอนเนกเตอร์มาตรฐานรวมอยู่แล้ว การเชื่อมต่อ ERP/TMS แบบเฉพาะอาจมีค่าใช้จ่ายเพิ่ม" },
  ],

  compareTitle: "CargoNova เทียบกับราคา TMS แบบดั้งเดิม",
  compareHeaders: ["", "TMS แบบดั้งเดิม", "CargoNova"],
  compareRows: [
    { label: "ค่าไลเซนส์ล่วงหน้า", legacy: "มักมีค่าใช้จ่ายก้อนใหญ่", cargonova: "ต้นทุนล่วงหน้าต่ำหรือไม่มีเลย" },
    { label: "การคิดค่าผู้ใช้", legacy: "คิดเพิ่มรายที่นั่ง", cargonova: "แบ่งเป็นระดับผู้ใช้ที่ยืดหยุ่น" },
    { label: "ค่าโมดูล", legacy: "โมดูลศุลกากร การเงิน ฯลฯ คิดเพิ่ม", cargonova: "เปิดใช้ Agent ตามความต้องการ" },
    { label: "การคาดการณ์ต้นทุน", legacy: "คาดการณ์ได้ยาก", cargonova: "ต้นทุนรายเดือนหรือรายปีคาดการณ์ได้" },
  ],

  benefitsTitle: "ทำไมฟอร์วาร์ดเดอร์จึงเลือกโมเดลนี้",
  benefits: [
    { metric: "ต่ำ", title: "ความเสี่ยงล่วงหน้า", body: "เริ่มจากขอบเขตที่ชัดเจนและขยายได้โดยไม่ต้องลงทุนก้อนใหญ่" },
    { metric: "ยืดหยุ่น", title: "ขยายตามปริมาณงาน", body: "ค่าใช้จ่ายเพิ่มตามการใช้งานจริง ไม่ใช่ตามจำนวนพนักงานเพียงอย่างเดียว" },
    { metric: "โปร่งใส", title: "ไม่มีค่าใช้จ่ายแอบแฝง", body: "อัตราสมัครสมาชิกและค่าใช้งานที่ชัดเจน ช่วยให้วางงบประมาณได้แม่นยำ" },
  ],

  faqTitle: "คำถามที่พบบ่อยเรื่องราคา",
  faqs: [
    {
      q: "มีให้ทดลองใช้ฟรีหรือไม่?",
      a: "โดยทั่วไปเราจะเริ่มจากการพูดคุยเรื่องความต้องการและสาธิตแบบเฉพาะ หลังการประเมินเบื้องต้น ฟอร์วาร์ดเดอร์ที่เข้าเงื่อนไขสามารถเข้าสู่ระยะนำร่องได้",
    },
    {
      q: "ต้องจ่ายค่าผู้ใช้ทุกคนหรือไม่?",
      a: "ราคาแบ่งตามระดับผู้ใช้ ทีมขนาดเล็กจึงเพิ่มพนักงานปฏิบัติการหนึ่งถึงสองคนช่วงฤดูงานหนักได้โดยไม่มีค่าใช้จ่ายที่พุ่งสูงเกินไป",
    },
    {
      q: "การเชื่อมต่อระบบรวมอยู่ในราคาหรือไม่?",
      a: "คอนเนกเตอร์มาตรฐานสำหรับสายเรือ แพลตฟอร์มอีคอมเมิร์ซ และระบบบัญชีทั่วไปรวมอยู่แล้ว การเชื่อมต่อ ERP แบบเฉพาะที่ซับซ้อนอาจต้องใช้บริการเพิ่มเติม",
    },
  ],

  ctaTitle: "ขอใบเสนอราคาแบบเฉพาะ",
  ctaBody:
    "แจ้งขนาดทีม ปริมาณงานขนส่ง และความต้องการด้านการเชื่อมต่อระบบ เราจะจัดทำข้อเสนอที่ชัดเจนโดยไม่มีค่าใช้จ่ายแอบแฝง",
  ctaButton: "ขอใบเสนอราคา",
  ctaLink: "https://www.deep-insights.ai/book-demo",
  backLabel: "กลับสู่หน้าแรก",
  backTo: "/th",
  disclaimer:
    "ข้อมูลราคาข้างต้นเป็นเพียงข้อมูลอ้างอิงเท่านั้น ราคาสุดท้ายจะยืนยันในข้อเสนออย่างเป็นทางการตามความต้องการเฉพาะของคุณ",
};

const Pricing = () => {
  const { language } = useLanguage();
  const isZh = language === "zh";
  const isTh = language === "th";
  const c = isZh ? contentZh : isTh ? contentTh : contentEn;

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: c.faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://cargonova.ai/" },
      { "@type": "ListItem", position: 2, name: "Pricing", item: CANONICAL },
    ],
  };

  const pageTitle = isZh
    ? "CargoNova 价格计费 | 货代云平台订阅模式"
    : isTh
    ? "CargoNova Pricing | โมเดลค่าใช้จ่ายสำหรับฟอร์วาร์ดเดอร์"
    : "CargoNova Pricing | Cloud Platform Subscription for Freight Forwarders";

  const pageDescription = isZh
    ? "了解 CargoNova 云平台订阅与按用量计费模式，以及影响价格的因素和与传统 TMS 的成本对比。"
    : isTh
    ? "เรียนรู้โมเดล subscription และ usage-based pricing ของ CargoNova ปัจจัยที่มีผลต่อราคา และการเปรียบเทียบกับ legacy TMS"
    : "Learn about CargoNova's cloud platform subscription and usage-based pricing, cost factors and how it compares to legacy TMS licensing.";

  const pageCanonical = isZh ? CANONICAL_ZH : isTh ? CANONICAL_TH : CANONICAL;

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title={pageTitle}
        description={pageDescription}
        canonical={CANONICAL}
        canonicalZh={CANONICAL_ZH}
        canonicalTh={CANONICAL_TH}
        jsonLd={[articleSchema, faqSchema, breadcrumbSchema]}
      />

      <Navbar />

      <main className="relative pt-24 pb-20 overflow-hidden bg-gradient-to-tr from-secondary/10 via-background to-accent/10">
        <div className="absolute -top-20 -right-20 w-[36rem] h-[36rem] bg-secondary/20 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute top-1/3 -left-32 w-[32rem] h-[32rem] bg-accent/15 rounded-full blur-3xl pointer-events-none" />

        {/* Hero */}
        <section className="relative container mx-auto px-4 max-w-5xl pt-6 md:pt-10 pb-5 md:pb-8">
          <span className="inline-block py-1 px-2.5 rounded-full bg-secondary/10 text-secondary text-[11px] font-semibold tracking-wider uppercase mb-4 ring-1 ring-secondary/20">
            {c.eyebrow}
          </span>
          <h1
            className={`text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight mb-3 ${
              isZh ? "leading-[1.5159375]" : "leading-[1.25]"
            } bg-gradient-to-r from-foreground via-secondary to-foreground bg-clip-text text-transparent whitespace-pre-line`}
          >
            {c.title}
          </h1>
          <p className="text-sm md:text-base text-muted-foreground leading-relaxed mb-3">
            {c.summary}
          </p>
          <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
            {c.intro}
          </p>
        </section>

        {/* Pricing model cards */}
        <section className="relative container mx-auto px-4 max-w-5xl py-8 md:py-12">
          <h2 className="text-xl md:text-2xl font-bold text-foreground mb-6">
            {c.modelTitle}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {c.models.map((m, idx) => (
              <div
                key={idx}
                className="group relative overflow-hidden rounded-2xl bg-card ring-1 ring-border/60 shadow-xs hover:shadow-md transition-all duration-300 p-6"
              >
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-secondary to-accent" />
                <h3 className="text-base font-semibold text-foreground mb-3 leading-snug">
                  {m.title}
                </h3>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  {m.body}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Factors */}
        <section className="relative container mx-auto px-4 max-w-5xl py-8 md:py-12">
          <h2 className="text-xl md:text-2xl font-bold text-foreground mb-6">
            {c.factorsTitle}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {c.factors.map((f) => {
              const Icon = f.icon;
              return (
                <div
                  key={f.label}
                  className="group relative overflow-hidden rounded-2xl bg-card ring-1 ring-border/60 shadow-xs hover:shadow-md transition-all duration-300 p-6"
                >
                  <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-secondary to-accent" />
                  <div className="flex items-center gap-3 mb-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-secondary/10 text-secondary">
                      <Icon className="h-4 w-4" />
                    </span>
                    <h3 className="text-base font-semibold text-foreground leading-snug">
                      {f.label}
                    </h3>
                  </div>
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    {f.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </section>

        {/* Comparison table */}
        <section className="relative container mx-auto px-4 max-w-5xl py-8 md:py-12">
          <h2 className="text-xl md:text-2xl font-bold text-foreground mb-6">
            {c.compareTitle}
          </h2>
          <div className="overflow-x-auto rounded-2xl ring-1 ring-border/60 bg-card/70 backdrop-blur">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-secondary/10 text-foreground">
                  {c.compareHeaders.map((h, i) => (
                    <th
                      key={i}
                      className={`text-left font-semibold px-4 py-3 border-b border-border/50 ${
                        i === 2 ? "text-secondary" : ""
                      }`}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {c.compareRows.map((row, r) => (
                  <tr key={r} className="odd:bg-background/50">
                    <td className="px-4 py-3 border-b border-border/40 align-top font-medium text-foreground">
                      {row.label}
                    </td>
                    <td className="px-4 py-3 border-b border-border/40 align-top text-muted-foreground">
                      {row.legacy}
                    </td>
                    <td className="px-4 py-3 border-b border-border/40 align-top text-foreground">
                      <span className="inline-flex items-center gap-1.5">
                        <Check className="h-3.5 w-3.5 text-secondary" />
                        {row.cargonova}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Benefits */}
        <section className="relative container mx-auto px-4 max-w-5xl py-8 md:py-12">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-6">
            {c.benefitsTitle}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {c.benefits.map((b) => (
              <div
                key={b.title}
                className="group relative overflow-hidden rounded-2xl bg-card ring-1 ring-border/60 shadow-xs hover:shadow-md transition-all duration-300 p-6"
              >
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-secondary to-accent" />
                <div className="text-3xl md:text-4xl font-extrabold bg-gradient-to-r from-secondary to-accent bg-clip-text text-transparent mb-2 leading-none">
                  {b.metric}
                </div>
                <h3 className="text-base md:text-lg font-semibold text-foreground mb-2 leading-snug">
                  {b.title}
                </h3>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  {b.body}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="relative container mx-auto px-4 max-w-5xl py-8 md:py-12">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-6">
            {c.faqTitle}
          </h2>
          <div className="flex flex-col gap-3">
            {c.faqs.map((f) => (
              <details
                key={f.q}
                className="group rounded-2xl bg-card/80 backdrop-blur ring-1 ring-border/50 p-5 open:ring-secondary/50"
              >
                <summary className="cursor-pointer list-none flex items-center justify-between gap-4">
                  <span className="text-base font-semibold text-foreground">
                    {f.q}
                  </span>
                  <ChevronDown className="h-4 w-4 text-secondary transition-transform group-open:rotate-180" />
                </summary>
                <p className="mt-3 text-sm md:text-[15px] leading-relaxed text-muted-foreground">
                  {f.a}
                </p>
              </details>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="relative container mx-auto px-4 max-w-3xl py-8">
          <div className="rounded-3xl bg-gradient-to-br from-secondary/15 via-card to-accent/10 ring-1 ring-secondary/30 p-6 md:p-8 shadow-[0_20px_60px_-24px_hsl(var(--secondary)/0.35)]">
            <h3 className="text-lg md:text-xl font-bold text-foreground mb-2">
              {c.ctaTitle}
            </h3>
            <p className="text-sm md:text-base text-muted-foreground mb-5">
              {c.ctaBody}
            </p>
            <a
              href={c.ctaLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-secondary to-accent px-6 py-3 text-sm font-semibold text-secondary-foreground shadow-lg shadow-secondary/30 transition-transform hover:-translate-y-0.5"
            >
              {c.ctaButton}
              <ArrowRight className="h-4 w-4" />
            </a>
            <div className="mt-5">
              <a
                href={c.backTo}
                className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-secondary transition-colors"
              >
                <ArrowRight className="h-3.5 w-3.5 rotate-180" />
                {c.backLabel}
              </a>
            </div>
          </div>
          <p className="mt-6 text-xs text-center text-muted-foreground max-w-2xl mx-auto">
            {c.disclaimer}
          </p>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Pricing;
